import { motion } from "framer-motion";
import { Check, ArrowUpRight, Sparkles } from "lucide-react";
import { useI18n } from "../../lib/i18n";

const priceData = [
    { price: "300", highlight: false, currency: "€" },
    { price: "500", highlight: true, currency: "€" },
    { price: "999", highlight: false, currency: "€" },
];

const cardVariant = {
    hidden: { opacity: 0, y: 40 },
    show: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function Pricing() {
    const { t } = useI18n();
    const s = t.pricing;

    return (
        <section
            id="pricing"
            className="section relative bg-[#050505] overflow-hidden"
            data-testid="pricing-section"
        >
            <div
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[800px] rounded-full"
                style={{
                    background:
                        "radial-gradient(ellipse at center, rgba(229,91,19,0.12) 0%, rgba(229,91,19,0.03) 30%, transparent 60%)",
                    filter: "blur(30px)",
                }}
            />

            <div className="container-x relative z-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 md:mb-20">
                    <div>
                        <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.28em] text-[#E55B13] mb-6">
                            <span className="w-8 h-px bg-[#E55B13]" />
                            {s.eyebrow}
                        </div>
                        <h2 className="font-heading text-4xl md:text-6xl font-light tracking-[-0.03em] leading-[1.02] max-w-3xl">
                            {s.titlePre}
                            <span className="italic font-extralight text-white/60">
                                {s.titleAccent}
                            </span>
                            {s.titlePost}
                        </h2>
                    </div>
                    <p className="font-body text-white/50 max-w-sm text-base leading-relaxed">
                        {s.lede}
                    </p>
                </div>

                <div
                    className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-stretch"
                    data-testid="pricing-grid"
                >
                    {s.plans.map((p, i) => {
                        const meta = priceData[i];
                        const n = String(i + 1).padStart(2, "0");
                        const testKey = ["starter", "growth", "scale"][i];
                        return (
                            <motion.div
                                key={i}
                                custom={i}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: "-80px" }}
                                variants={cardVariant}
                                className={`relative rounded-[28px] p-8 md:p-10 flex flex-col min-h-[560px] transition-transform duration-500 ${
                                    meta.highlight
                                        ? "bg-gradient-to-b from-[#E55B13]/[0.12] via-[#0a0a0a] to-[#0a0a0a] border border-[#E55B13]/40 lg:-translate-y-3 lg:scale-[1.02] shadow-[0_40px_120px_-40px_rgba(229,91,19,0.35)]"
                                        : "bg-gradient-to-b from-white/[0.03] to-transparent border border-white/[0.08] card-lift"
                                }`}
                                data-testid={`pricing-card-${testKey}`}
                            >
                                {meta.highlight && (
                                    <div className="absolute -top-3 left-8 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E55B13] text-black font-mono text-[10px] tracking-[0.22em] uppercase font-medium">
                                        <Sparkles size={12} strokeWidth={2.4} />
                                        {s.mostPopular}
                                    </div>
                                )}

                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/40 mb-2">
                                            {s.plan} · {n}
                                        </div>
                                        <h3 className="font-heading text-3xl md:text-4xl font-light text-white tracking-[-0.02em]">
                                            {p.name}
                                        </h3>
                                        <div className="mt-1 font-mono text-[11px] tracking-[0.2em] uppercase text-white/50">
                                            {p.tag}
                                        </div>
                                    </div>
                                    <span className="font-mono text-[11px] tracking-[0.28em] text-white/30">
                                        / {n}
                                    </span>
                                </div>

                                <div className="mt-10 mb-8">
                                    <div className="flex items-baseline gap-1">
                                        <span
                                            className={`font-heading text-2xl font-light ${meta.highlight ? "text-[#E55B13]" : "text-white/70"}`}
                                        >
                                            {meta.currency}
                                        </span>
                                        <span className="font-heading text-6xl md:text-7xl font-light text-white tracking-[-0.03em] leading-none">
                                            {meta.price}
                                        </span>
                                        <span className="font-body text-sm text-white/50 ml-2">
                                            {p.is3Month
                                                ? p.periodNote
                                                : s.perMonth}
                                        </span>
                                    </div>
                                    <div className="mt-2 font-mono text-[11px] tracking-[0.2em] uppercase text-white/40">
                                        {s.vat}
                                    </div>
                                </div>

                                <div className="hairline mb-6" />

                                <div className="mb-8">
                                    <div
                                        className={`font-heading text-2xl font-medium ${meta.highlight ? "text-white" : "text-white/90"} tracking-[-0.02em]`}
                                    >
                                        {p.videos}
                                    </div>
                                    {p.bonus && (
                                        <div className="mt-1 font-mono text-[11px] tracking-[0.16em] uppercase text-[#E55B13]">
                                            {p.bonus}
                                        </div>
                                    )}
                                </div>

                                <ul className="space-y-3 flex-1">
                                    {p.features.map((f) => (
                                        <li
                                            key={f}
                                            className="flex items-start gap-3 font-body text-[14px] text-white/70 leading-relaxed"
                                        >
                                            <span
                                                className={`shrink-0 mt-1 w-4 h-4 rounded-full flex items-center justify-center ${meta.highlight ? "bg-[#E55B13] text-black" : "bg-white/[0.06] text-white/70 border border-white/10"}`}
                                            >
                                                <Check
                                                    size={10}
                                                    strokeWidth={3}
                                                />
                                            </span>
                                            {f}
                                        </li>
                                    ))}
                                </ul>

                                <a
                                    href="#contact"
                                    className={`mt-10 inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 font-body text-sm font-medium transition-all duration-300 group ${
                                        meta.highlight
                                            ? "bg-[#E55B13] text-black hover:bg-[#ff6a1e]"
                                            : "bg-white/[0.04] text-white border border-white/10 hover:border-[#E55B13]/50 hover:bg-white/[0.08]"
                                    }`}
                                    data-testid={`pricing-cta-${testKey}`}
                                >
                                    {p.cta}
                                    <ArrowUpRight
                                        size={16}
                                        className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                                    />
                                </a>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{
                        duration: 1,
                        delay: 0.2,
                        ease: [0.16, 1, 0.3, 1],
                    }}
                    className="mt-16 md:mt-20 relative rounded-[28px] p-8 md:p-12 border border-white/[0.08] bg-gradient-to-b from-white/[0.02] to-transparent overflow-hidden"
                    data-testid="pricing-custom-block"
                >
                    {/* Ambient glow */}
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -top-20 -right-20 w-96 h-96 rounded-full"
                        style={{
                            background:
                                "radial-gradient(circle at center, rgba(229,91,19,0.14), transparent 60%)",
                            filter: "blur(30px)",
                        }}
                    />

                    <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                        <div className="lg:col-span-6">
                            <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.28em] text-[#E55B13] mb-6">
                                <span className="w-8 h-px bg-[#E55B13]" />
                                {s.custom.eyebrow}
                            </div>
                            <h3 className="font-heading text-3xl md:text-5xl font-light tracking-[-0.03em] leading-[1.02] text-white">
                                {s.custom.titlePre}
                                <span className="italic font-extralight text-[#E55B13]">
                                    {s.custom.titleAccent}
                                </span>
                            </h3>
                            <p className="mt-6 font-body text-white/60 text-base md:text-lg leading-relaxed max-w-lg">
                                {s.custom.body}
                            </p>

                            <a
                                href="#contact"
                                className="mt-10 btn-primary inline-flex items-center gap-2 group"
                                data-testid="pricing-custom-cta"
                            >
                                {s.custom.cta}
                                <ArrowUpRight
                                    size={16}
                                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                                />
                            </a>
                        </div>

                        <div className="lg:col-span-6">
                            <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-white/40 mb-5">
                                {s.custom.servicesLabel}
                            </div>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                                {s.custom.services.map((svc, i) => (
                                    <li
                                        key={svc}
                                        className="flex items-center gap-3 font-body text-[15px] text-white/80"
                                        data-testid={`pricing-custom-service-${i}`}
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#E55B13]" />
                                        {svc}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
